import { useEffect, useState } from 'react';
import { FORM_TYPES } from '../../utils/constants';
import { CustomModal } from '../CustomModal';
import { FormBuilder, FormBuilderSavedForm } from './FormBuilder';

export type FormBuilderOverlayFormType = typeof FORM_TYPES.INSPECTION | typeof FORM_TYPES.DAILY_SERVICE;

interface FormBuilderOverlayProps {
    visible: boolean;
    onDismiss: () => void;
    onFormSaved: (form: FormBuilderSavedForm) => void;
    defaultFormName?: string;
    header?: string;
    formType?: FormBuilderOverlayFormType;
}

const OVERLAY_COPY: Record<
    FormBuilderOverlayFormType,
    { header: string; allowedFormTypes: FormBuilderOverlayFormType[] }
> = {
    [FORM_TYPES.INSPECTION]: {
        header: 'New Inspection Form',
        allowedFormTypes: [FORM_TYPES.INSPECTION],
    },
    [FORM_TYPES.DAILY_SERVICE]: {
        header: 'New Daily Service Form',
        allowedFormTypes: [FORM_TYPES.DAILY_SERVICE],
    },
};

export const FormBuilderOverlay = ({
    visible,
    onDismiss,
    onFormSaved,
    defaultFormName = '',
    header,
    formType = FORM_TYPES.INSPECTION,
}: FormBuilderOverlayProps) => {
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [showExitModal, setShowExitModal] = useState(false);
    const [builderKey, setBuilderKey] = useState(0);

    const overlayCopy = OVERLAY_COPY[formType] || OVERLAY_COPY[FORM_TYPES.INSPECTION];
    const resolvedHeader = header || overlayCopy.header;

    useEffect(() => {
        if (!visible) {
            setHasUnsavedChanges(false);
            setShowExitModal(false);
            return;
        }
        setBuilderKey((key) => key + 1);
        setHasUnsavedChanges(false);
        setShowExitModal(false);
    }, [visible, formType]);

    const requestClose = () => {
        if (hasUnsavedChanges) {
            setShowExitModal(true);
            return;
        }
        onDismiss();
    };

    const handleConfirmExit = () => {
        setShowExitModal(false);
        setHasUnsavedChanges(false);
        onDismiss();
    };

    if (!visible) return null;

    return (
        <>
            <CustomModal
                visible={visible}
                onDismiss={requestClose}
                header={resolvedHeader}
                size="xlarge"
                preferBodyFocus
                bodyClassName="form-builder-modal-body"
            >
                <FormBuilder
                    key={builderKey}
                    variant="overlay"
                    isNewForm
                    lockFormType
                    defaultFormType={formType}
                    defaultFormName={defaultFormName}
                    allowedFormTypes={overlayCopy.allowedFormTypes}
                    manageNavigationGuard={false}
                    onDirtyChange={setHasUnsavedChanges}
                    onCancel={onDismiss}
                    onSaved={async (form, status) => {
                        setHasUnsavedChanges(false);
                        onFormSaved(form);
                        if (status === 'published') {
                            onDismiss();
                        }
                    }}
                />
            </CustomModal>

            {showExitModal && (
                <CustomModal
                    visible={showExitModal}
                    onDismiss={() => setShowExitModal(false)}
                    header="Unsaved Changes"
                    footer={
                        <div className="modal-footer-actions">
                            <button type="button" className="btn-cancel" onClick={() => setShowExitModal(false)}>
                                Cancel
                            </button>
                            <button type="button" className="btn-publish" onClick={handleConfirmExit}>
                                Leave
                            </button>
                        </div>
                    }
                >
                    <p>You have unsaved changes. Are you sure you want to leave?</p>
                </CustomModal>
            )}
        </>
    );
};
